import React, { useState, useRef, useMemo } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// EmailTagInput — chip-style multi-email input over a comma-separated string.
//   value       — 'a@x.com,b@y.com' (source of truth lives in the parent)
//   onChange    — called with the updated comma-joined string
//   suggestions — optional ['someone@x.com', …] for type-ahead. Filtered as you
//                 type, already-added addresses excluded. Click a suggestion, or
//                 move the highlight with the arrow keys and press Enter; Escape
//                 dismisses. While the typed text is not yet a full address the
//                 first suggestion is highlighted, so "sh" + Enter adds it.
// Enter, comma, or blur commits a fully typed email; anything invalid stays in
// the box with an error; Backspace on an empty input removes the last chip.
// (Shared version of the inline component used on Requirements/Clients pages.)
// ─────────────────────────────────────────────────────────────────────────────
const MAX_SUGGESTIONS = 6;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// PREFIX matching, not substring: typing "a" should offer abhinav@…, not every
// address that happens to contain an "a" somewhere. Each dot/hyphen/underscore-
// separated part of the local name is its own starting point, so "vi" and
// "prasad" both find vishnu.prasad@… while "a" does not.
const matchesQuery = (email, q) => {
  if (email.startsWith(q)) return true;
  const local = email.split('@')[0];
  return local.split(/[._+-]/).some(seg => seg.startsWith(q));
};

export default function EmailTagInput({
  value = '', onChange, placeholder, isDark, label, required, suggestions = [],
}) {
  const tags = value ? value.split(',').map(e => e.trim()).filter(Boolean) : [];
  const [input, setInput] = useState('');
  const [invalid, setInvalid] = useState('');
  const [open, setOpen] = useState(false);
  // The suggestion chosen with the arrow keys or mouse; -1 = none chosen yet.
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);

  // Nothing is offered until something is typed — an unprompted list on focus
  // just covers the field below it.
  const matches = useMemo(() => {
    const typed = input.trim().toLowerCase();
    if (!typed) return [];
    const already = new Set(tags.map(t => t.toLowerCase()));
    return (suggestions || [])
      .map(s => (typeof s === 'string' ? s : s?.email))
      .filter(Boolean)
      .map(s => String(s).toLowerCase())
      .filter(s => !already.has(s) && matchesQuery(s, typed))
      .slice(0, MAX_SUGGESTIONS);
  }, [input, suggestions, value]); // eslint-disable-line

  // What Enter adds. An explicit choice wins. Otherwise the first suggestion is
  // highlighted, unless the typed text is already a complete address, in which
  // case Enter adds exactly that instead of swapping in a lookalike.
  const list = open ? matches : [];
  const highlighted = !list.length ? -1
    : active >= 0 ? Math.min(active, list.length - 1)
    : EMAIL_RE.test(input.trim().toLowerCase()) ? -1 : 0;

  const addTag = (raw) => {
    // Accept pasted lists ("a@x.com, b@y.com; c@z.com") and copied display forms
    // ("<john@x.com>") — split on separators, strip wrappers, commit each valid
    // address, and leave only the genuinely invalid parts behind with an error.
    const parts = String(raw).split(/[,;\s]+/)
      .map(p => p.trim().toLowerCase().replace(/^<+|>+$/g, ''))
      .filter(Boolean);
    if (!parts.length) return;
    const valid = [], bad = [];
    for (const p of parts) (EMAIL_RE.test(p) ? valid : bad).push(p);
    const next = [...tags];
    const seen = new Set(tags.map(t => t.toLowerCase()));
    for (const v of valid) if (!seen.has(v)) { seen.add(v); next.push(v); }
    if (next.length !== tags.length) onChange(next.join(','));
    if (bad.length) { setInvalid(bad[0]); setInput(bad.join(', ')); }
    else { setInvalid(''); setInput(''); }
    setActive(-1);
  };

  const pick = (email) => {
    const e = String(email).trim().toLowerCase();
    if (!tags.some(t => t.toLowerCase() === e)) onChange([...tags, e].join(','));
    setInput(''); setInvalid(''); setActive(-1); setOpen(false);
    inputRef.current?.focus();
  };

  const removeTag = (idx) => onChange(tags.filter((_, i) => i !== idx).join(','));

  const onKeyDown = (e) => {
    if (e.nativeEvent?.isComposing) return; // IME still composing a character
    if (e.key === 'ArrowDown' && list.length) {
      e.preventDefault(); setActive((highlighted + 1) % list.length); return;
    }
    if (e.key === 'ArrowUp' && list.length) {
      e.preventDefault(); setActive(highlighted <= 0 ? list.length - 1 : highlighted - 1); return;
    }
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); setActive(-1); return; }
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (highlighted >= 0) pick(list[highlighted]);
      else addTag(input);
      return;
    }
    if (e.key === 'Backspace' && !input && tags.length) removeTag(tags.length - 1);
  };

  const base = {
    fontSize: 13,
    background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
    border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#c7d2fe'}`,
    color: isDark ? '#f1f5f9' : '#111827',
  };

  return (
    <div style={{ position: 'relative' }}>
      {label && (
        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
          {label}{required ? ' *' : ''}
        </label>
      )}
      <div
        onClick={() => inputRef.current?.focus()}
        style={{ ...base, borderRadius: 8, padding: '6px 10px', display: 'flex', flexWrap: 'wrap', gap: 5, cursor: 'text', minHeight: 40, alignItems: 'center' }}
      >
        {tags.map((tag, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff', color: isDark ? '#a5b4fc' : '#4338ca', border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}` }}>
            {tag}
            <span onClick={e => { e.stopPropagation(); removeTag(i); }} style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1, color: isDark ? '#818cf8' : '#6366f1', marginLeft: 1 }}>×</span>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={e => { setInput(e.target.value); setActive(-1); setOpen(true); if (invalid) setInvalid(''); }}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          // Clicking a suggestion never blurs the input (see the list's
          // onMouseDown), so blur means the user really left the field.
          onBlur={() => { setOpen(false); setActive(-1); addTag(input); }}
          autoComplete="off"
          spellCheck={false}
          placeholder={tags.length === 0 ? (placeholder || 'Type email and press Enter…') : ''}
          style={{ flex: 1, minWidth: 160, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: isDark ? '#f1f5f9' : '#111827' }}
        />
      </div>

      {list.length > 0 && (
        <div
          // Keep focus in the input while clicking here. Without this the input
          // blurs first, the list closes, and the click lands on nothing.
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'absolute', zIndex: 1000, left: 0, right: 0, marginTop: 4,
            background: 'var(--surface-1, #fff)', border: '1px solid var(--border-muted)',
            borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            maxHeight: 220, overflowY: 'auto',
          }}
        >
          {list.map((m, i) => (
            <button
              key={m}
              type="button"
              tabIndex={-1}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(m)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', border: 'none',
                cursor: 'pointer', padding: '8px 12px', fontSize: 12.5,
                background: i === highlighted ? 'var(--hover-bg)' : 'transparent',
                color: 'var(--text-primary)',
              }}
            >
              {m}
            </button>
          ))}
          <div style={{ padding: '6px 12px', fontSize: 10.5, color: 'var(--text-muted)', borderTop: '1px solid var(--border-subtle)' }}>
            Previously used · click, or ↑↓ and Enter to add
          </div>
        </div>
      )}

      {invalid && (
        <p style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
          “{invalid}” is not a valid email address — fix it and press Enter to add.
        </p>
      )}
    </div>
  );
}
